import styles from "../Faqs.module.css";
import Link from "next/link";

export interface Faq {
  category: string;
  items: {
    question: React.ReactNode;
    answer: React.ReactNode;
  }[];
}

export const faqs: Faq[] = [
  {
    category: "General Questions",
    items: [
      {
        question: <>Which query engine will I be billed on?</>,
        answer: (
          <>
            Queries and CSV downloads on our Dune Engine V2 engines and Postgres
            will be billed.
          </>
        ),
      },
      {
        question: <>Where can I see my billing history?</>,
        answer: (
          <>
            Your billing history can be found under "Billing" in your
            Subscription Settings. Follow the steps below for a complete view:
            <ol>
              <li>
                Go to{" "}
                <Link href="/">
                  <a target="_blank">dune.com</a>
                </Link>
              </li>
              <li>Sign in to your Dune account</li>
              <li>
                Click your avatar in the middle of the top bar where your user
                or team badge is
              </li>
              <li>Click the Settings icon and then “Subscription”</li>
            </ol>
          </>
        ),
      },
      {
        question: <>How are upgrades and downgrades handled?</>,
        answer: (
          <>
            Upgrading to a new paid tier resets your billing cycle start date.
            On the date of your upgrade, you will be billed the full new plan
            price and refunded a prorated amount corresponding to the number of
            unused days of your current plan. If you upgrade from Plus to
            Premium on day 15 of your billing cycle, you will be billed a month
            of Premium minus 15 days of Plus (roughly half the monthly Plus
            cost).
            <br />
            <br />
            Downgrades to lower tiers are applied at the end of your billing
            cycle. Benefits from your current tier remain until the end of your
            billing cycle. At the end of your billing cycle, you will
            automatically be downgraded to the lower tier of your choice.
          </>
        ),
      },
      {
        question: (
          <>How do I upgrade, downgrade, or cancel my team’s subscription?</>
        ),
        answer: (
          <>
            Only administrators of teams with paid plans can change team
            subscriptions. To change your team’s subscription:
            <ol>
              <li>
                Go to{" "}
                <Link href="/">
                  <a target="_blank">dune.com</a>
                </Link>
              </li>
              <li>Sign in to your Dune account</li>
              <li>
                Click your avatar in the middle of the top bar where your team
                badge is
              </li>
              <li>Click the Settings icon and then “Subscription”</li>
              <li>Click the plan you want to upgrade or downgrade to</li>
            </ol>
            <br />
            To cancel your subscription, follow these steps:
            <ol>
              <li>
                Go to{" "}
                <Link href="/">
                  <a target="_blank">dune.com</a>
                </Link>
              </li>
              <li>Sign in to your Dune account</li>
              <li>
                Click your avatar in the middle of the top bar where your user
                or team badge is
              </li>
              <li>Click the Settings icon and then “Subscription”</li>
              <li>Change your plan to Free</li>
            </ol>
            <br />
            Your subscription will remain active for 30 days after your last
            payment.
          </>
        ),
      },
      {
        question: <>Do you offer paid plans for individuals?</>,
        answer: (
          <>
            Currently, our paid plans are geared towards teams but single users
            can pay for additional credits as their usage grows. If you are a
            single user with higher requirements than what's offered in the free
            tier, you can create a team of one to access paid tiers.
          </>
        ),
      },
      {
        question: <>What if I have more questions?</>,
        answer: (
          <p>
            For general info on how to use Dune, please see our{" "}
            <Link href="https://dune.com/docs/">
              <a target="_blank">docs</a>
            </Link>
            . For specific questions on how to do a query, please join our
            Discord. If you have other issues or questions not listed on this
            page, please contact{" "}
            <a href="mailto:support@dune.com">support@dune.com</a>.
          </p>
        ),
      },
    ],
  },
  {
    category: "Credits and spending",
    items: [
      {
        question: <>What is a credit?</>,
        answer: (
          <>
            Credits are how we calculate paid usage on Dune. They allow you to
            flexibly use Dune to your needs across query performance,
            programmatic executions and API exports.
          </>
        ),
      },
      {
        question: <>Can I view my credit usage and balance?</>,
        answer: (
          <>
            To view your credit usage and balance go to Settings and select
            Subscription; here you will see used and remaining credits.
          </>
        ),
      },
      {
        question: <>How do I calculate my potential monthly credit usage?</>,
        answer: (
          <>
            Calculate potential usage and spend based on your needs. Below is a
            breakdown of credit usage:
            <br />
            <br />
            <strong>APP</strong>
            <i>Boost query execution</i>
            Medium = 10 credits
            <br />
            Large = 20 credits
            <br />
            <br />
            <strong>API</strong>
            <i>Programmatic execution</i>
            Default = Medium = 10 credits
            <br />
            Large = 20 credits
            <br />
            <br />
            <i>API export</i>1 credit = 1000 data points
          </>
        ),
      },
      {
        question: <>What happens if my credit quota runs out?</>,
        answer: (
          <div>
            Once your quota runs out we will bill you for extra credits. See
            “Extra credits” in the <a href="#pricing-table">Pricing table</a> to
            understand costs.
          </div>
        ),
      },

      {
        question: <>Can I purchase extra credits in bulk?</>,
        answer: (
          <>
            No, once your quota runs out, extra credits will be billed on an
            ongoing basis as you use Dune. Credits are not available to purchase
            in bulk once you reach your quota.
          </>
        ),
      },
      {
        question: (
          <>
            If I have leftover credits at the end of the billing cycle can I use
            them later?
          </>
        ),
        answer: (
          <>
            Remaining credits at the end of a billing cycle do not rollover to
            the next month.
          </>
        ),
      },
      {
        question: <>Can I “pay as I go” without upgrading?</>,
        answer: (
          <div>
            Yes, you can pay as you go. Both individuals and teams in the free
            tier can accrue usage credits as they go. See "Extra credits" in the{" "}
            <a href="#pricing-table">Pricing table</a> to understand costs.
          </div>
        ),
      },
      {
        question: <>How can I limit my spending?</>,
        answer: (
          <>
            To limit monthly spend, go to your subscription page in Settings and
            set the limit to the desired amount. Setting your limit to $0 will
            prevent additional usage billing.
          </>
        ),
      },
      {
        question: (
          <>What if I lower my spend limit below my current spend level?</>
        ),
        answer: (
          <>
            If you lower your spend limit below your current spending level, you
            will not be charged any additional fees beyond your new spend limit.
            However, any usage that has accrued does not rollover and will still
            be billed based on the previous spend limit.
          </>
        ),
      },
    ],
  },
  {
    category: "Query Engines & Executions",
    items: [
      {
        question: <>How is my query execution usage counted?</>,
        answer: (
          <>
            When you manually trigger an execution by pressing Run in the Query
            Editor, dashboard, or via the API, we accrue that usage to the user
            or team account. To ensure you are accruing usage in the correct
            account, view your selected account in the account switcher located
            in the middle of the top navigation bar on Dune.
          </>
        ),
      },
      {
        question: <>What are the differences between query engine sizes?</>,
        answer: (
          <>
            We have three different query engine sizes that you can use on Dune:
            <br />
            <ul className={styles.list}>
              <li>
                Free (0 credits): Run queries manually in the Dune app for free,
                in a Medium size query engine with no guarantees and a maximum
                of three (3) ongoing executions.
              </li>
              <li>
                Medium (10 credits): Run fast queries more reliably, manually or
                programmatically. No limit for ongoing executions.
              </li>
              <li>
                Large (20 credits): Speed up your more complex workloads,
                manually or programmatically. No limit for ongoing executions.
              </li>
            </ul>
            <br />
            Roughly speaking, the Large query engine has 2x the amount of
            computational capacity per query than the Medium tier. Overall, the
            higher the tier, the faster your queries will run. Exact execution
            times will vary depending on query complexity and cluster load.
          </>
        ),
      },
      {
        question: <>What is a programmatic execution?</>,
        answer: (
          <>
            Programmatic executions are query executions done through the Dune
            API, scheduled executions, or dashboard runs. Programmatic
            executions vary in credit costs depending on the query engine size
            you select.
          </>
        ),
      },
      {
        question: <>What are the limitations of the Free query engine size?</>,
        answer: (
          <>
            The Free query engine on Dune is available to anyone at no cost, but
            it does come with limitations. First, there are no performance or
            queue priority guarantees, which means executions may take longer to
            run during times of high demand. Additionally, the Free query engine
            is limited to manual query editor executions only; automated
            executions (API, scheduling, refreshing) are not available. Lastly,
            there is a maximum limit of three (3) ongoing executions (executing
            or queued) at any given time for Free executions.
          </>
        ),
      },
    ],
  },
  {
    category: "Features",
    items: [
      {
        question: <>Can I add a private query to a public dashboard?</>,
        answer: (
          <>
            If you add a private query to a public dashboard, other Dune users
            will be able to see the visualization in the public dashboard, but
            won't see your code when they click the query. The results table,
            other visualizations you might have, and the underlying query are
            all private.
          </>
        ),
      },
      {
        question: <>Do archived private queries count towards my limit?</>,
        answer: (
          <>
            No, you can archive private queries that you no longer need to
            increase your existing private queries quota. Archived queries are
            not accessible by anyone else and cannot be run. You can unarchive
            them later if you change your mind.
          </>
        ),
      },
      {
        question: <>Can I embed private queries on other websites?</>,
        answer: (
          <>
            Yes, anyone visiting the website with the embed can see the
            visualization you chose to embed and nothing else.
          </>
        ),
      },
      {
        question: <>As a Premium team, what will happen to watermarks?</>,
        answer: (
          <>
            When logged in to the team on Dune, work you’ve created will not
            have watermarks. Public dashboards URLs always have watermarks.
          </>
        ),
      },
    ],
  },
  {
    category: "Teams",
    items: [
      {
        question: <>What is an Editor seat?</>,
        answer: (
          <>
            Owners of editor seats can create and edit content owned by their
            team, but they cannot access team management features; those are
            reserved for admins. Viewers have read-only access to the team’s
            content and can still benefit from their team plan features and
            quotas.
          </>
        ),
      },
      {
        question: <>Do my team’s benefits apply to all content on Dune?</>,
        answer: (
          <>
            Yes, team benefits apply to all Dune content, as long as your team
            is in the correctly selected account which is viewable in the middle
            of the top navigation bar on Dune.
          </>
        ),
      },
      {
        question: <>How is a team’s usage or feature quota applied?</>,
        answer: <>Quotas are applied to all team usage.</>,
      },
      {
        question: <>Can I invite unlimited collaborators?</>,
        answer: (
          <>
            Yes, you can invite as many people as you need into your Dune team
            at no additional cost. All team members will need a Dune account to
            collaborate with you.
          </>
        ),
      },
      {
        question: <>Can I create unlimited Free teams?</>,
        answer: <>You can create up to 3 Free teams on Dune.</>,
      },
    ],
  },
  {
    category: "API",
    items: [
      {
        question: <>What is a datapoint?</>,
        answer: (
          <>
            A datapoint can, in most cases, be thought of as rows * columns with
            an additional limit of 100 average bytes per cell in a set of
            results. This can be expressed as:
            <br />
            <br />
            <p className={styles.code}>
              datapoints = max(rows * columns, ceil(total_bytes / 100))
            </p>
          </>
        ),
      },
      {
        question: (
          <>Do I get charged data points for every time I read a result?</>
        ),
        answer: (
          <>
            We charge the data points in the result for the 1st read result of
            every distinct query execution and every subsequent 100th read per
            billing cycle.
          </>
        ),
      },
      {
        question: <>How do query executions work in the API?</>,
        answer: (
          <>
            Query executions are identical to those triggered on dune.com.
            Please note: free executions are not supported via the API.
          </>
        ),
      },
      {
        question: <>Can my team use the API?</>,
        answer: (
          <>
            Yes, the API is included in all Dune plans. This means that all
            teams and users, including those in the Free plan, can access the
            API to trigger executions and export data, provided their spend
            limit has not been exceeded. Please note: credits still apply for
            API usage, regardless of the plan you are on.
          </>
        ),
      },
      {
        question: <>Should I link back to the source when using the API?</>,
        answer: (
          <>
            We strongly believe Dune wizards should be credited for their work’s
            impact. We encourage you to credit the wizard, or perhaps to link to
            the original query. Linking to transparent data sources like Dune is
            a great way of building trust.
          </>
        ),
      },
    ],
  },
];
